
import React from "react"
import { Outlet, useNavigate } from "react-router-dom"
import Navbar from "./Navbar"
import PHMISMenubar, { MENUBAR_GROUPS } from "./PHMISMenubar"
import Footer from "./Footer"
import PageContainer from "./PageContainer"
import FloatingChatWidget from "./chat/FloatingChatWidget"
import { ChatProvider } from "../features/chat/ChatProvider"
import EmergencyAlertModal from "./EmergencyAlertModal"
import { clearAuthSession, useAuthSession } from "../lib/auth/session"
import { notifySessionChange } from "../lib/session"
import { PATHS } from "../routes/paths"
import { getModuleDefinition, type ModuleId } from "../config/moduleRegistry"
import { useFacilityAccess } from "../lib/FacilityAccessContext"

export default function Layout() {
  const { displayName, profile } = useAuthSession()
  const { canUseModule } = useFacilityAccess()
  const navigate = useNavigate()
  const [showEmergencyModal, setShowEmergencyModal] = React.useState(false)
  const handleSignOut = React.useCallback(() => {
    clearAuthSession()
    notifySessionChange()
    navigate(PATHS.AUTH_LOGIN, { replace: true })
  }, [navigate])
  const openEmergencyModal = React.useCallback(() => setShowEmergencyModal(true), [])
  const closeEmergencyModal = React.useCallback(() => setShowEmergencyModal(false), [])
  const roleCode = profile?.roleCode || profile?.userRole || null

  const isPathMatch = React.useCallback((currentPath: string, targetPath: string) => {
    if (!currentPath || !targetPath) return false
    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const pattern = targetPath
      .split("/")
      .map((segment) => (segment.startsWith(":") ? "[^/]+" : escapeRegex(segment)))
      .join("/")
    const regex = new RegExp(`^${pattern}(?:/.*)?$`)
    return regex.test(currentPath)
  }, [])

  const activeMenuLabel = React.useMemo(() => {
    const path = window.location.pathname
    for (const group of MENUBAR_GROUPS) {
      if (group.type === "link") {
        const module = getModuleDefinition(group.link.moduleId)
        if (!module || !canUseModule(module.id as ModuleId)) continue
        const routeKey = group.link.route ?? module.routes[0]
        const to = routeKey ? PATHS[routeKey] : ""
        if (to && isPathMatch(path, to)) {
          return group.label
        }
        continue
      }
      for (const section of group.sections) {
        for (const link of section.links) {
          const module = getModuleDefinition(link.moduleId)
          if (!module || !canUseModule(module.id as ModuleId)) continue
          const routeKey = link.route ?? module.routes[0]
          const to = routeKey ? PATHS[routeKey] : ""
          if (to && isPathMatch(path, to)) {
            return group.label
          }
        }
      }
    }
    return null
  }, [canUseModule, isPathMatch])

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white">
        <Navbar
          onSignOut={handleSignOut}
          onEmergencyAlert={openEmergencyModal}
          chatWidget={
            <ChatProvider>
              <FloatingChatWidget variant="navbar" />
            </ChatProvider>
          }
          user={{
            name: profile?.fullName || displayName || "Officer",
            role: profile?.roleCode || profile?.assignment || "Port Health Officer",
            email: profile?.email || undefined,
            phone: profile?.mobileNumber || undefined,
            dutyStation: profile?.portOfEntry || undefined,
          }}
        />
        <PHMISMenubar roleCode={roleCode} />
      </header>
      <main className="flex-1 overflow-y-auto p-0">
        <PageContainer>
          {activeMenuLabel && (
            <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-indigo-700">
                {activeMenuLabel}
              </span>
              <span className="text-slate-400">Currently active</span>
            </div>
          )}
          <Outlet />
        </PageContainer>
      </main>
      <Footer />
      <EmergencyAlertModal open={showEmergencyModal} onClose={closeEmergencyModal} />
    </div>
  )
}
 
